import { Box, Button, Input, Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/react";
import {
  ColorIcon,
  colors,
  customEvent,
  Field,
  getCookie,
  HStack,
  VStack,
  EventPathType,
} from "goi_common";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { formatPhoneNumber } from "@/utils/formatIdentifyNumber";
import { postLeadConsentRecord } from "@/apis/leadConsentRecord";
import { useSubmitLead } from "@/hooks/useSubmitLead";
import { mubinsoLeadStatus } from "@/containers/MatchContainer/components/MatchRequest/Mubinso/mubinsoConstants";
import { useGetServerTime } from "@/hooks/useGetServerTime";

export default function FileRequestModal({
  isOpen,
  onClose,
  path = "parlor_detail",
  title = "체크리스트 받기",
  description = "장례 비용 예상 견적표, 임종 준비 체크리스트를 제공해드리고 있습니다.",
  isTitleIcon = true,
  isStatusSelect = true,
  isAdditionalInfoSelect = false,
  buttonText = "동의하고 자료 받아보기",
  defaultMemo = "",
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  path?: keyof typeof EventPathType;
  title?: string;
  description?: string;
  isTitleIcon?: boolean;
  isStatusSelect?: boolean;
  isAdditionalInfoSelect?: boolean;
  buttonText?: string;
  defaultMemo?: string;
  onSuccess?: () => void;
}) {
  const [value, setValue] = useState("");
  const [memo, setMemo] = useState(defaultMemo);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const { data: serverTime } = useGetServerTime();
  const isNight = serverTime?.is_night ?? false;

  const router = useRouter();
  const { submitLead } = useSubmitLead();

  useEffect(() => {
    if (!isOpen) {
      setIsCompleted(false);
    }
  }, [isOpen]);

  const handleClick = useCallback(() => {
    customEvent({
      newGtm: {
        path,
        trigger_and_target: "file_request_mdl-try_submit",
      },
    });

    if (!value) {
      alert("번호를 입력해주세요.");
      return;
    }
    if (!memo && isStatusSelect) {
      alert("장례 준비 상황을 알려주세요.");
      return;
    }
    if (!/^010-\d{4}-\d{4}$/.test(value)) {
      alert("번호 형식을 확인해주세요. (010-****-****)");
      return;
    }

    submitLead({
      phone: value,
      lead_type: "checklist",
      slack_message: `*페이지* ${window.location.origin}${router.asPath}\n*리퍼러* ${
        getCookie("referrer") ?? ""
      }`,
      status: memo ? mubinsoLeadStatus(memo) : undefined,
      accept_night_call: isNight ? true : undefined,
    })
      .then(() => {
        customEvent({
          newGtm: {
            path,
            trigger_and_target: "file_request_mdl-submit_success",
            text: value,
          },
        });
        onSuccess?.();
        setIsCompleted(true);
      })
      .catch(() => {
        alert("번호를 확인해주세요.");
      });
  }, [value, memo, isStatusSelect, path, router.asPath, onSuccess, isNight, submitLead]);

  const handleMarketingAgree = useCallback(() => {
    customEvent({
      newGtm: {
        path,
        trigger_and_target: "file_request_mdl-marketing_agree",
      },
    });
    postLeadConsentRecord({
      phone: value,
      agreement: { marketing_agreed: true },
    }).catch((e) => console.error("마케팅 수신동의 기록 실패:", e));
    onClose();
  }, [value, path, onClose]);

  const handleClose = useCallback(() => {
    customEvent({
      newGtm: {
        path,
        trigger_and_target: "file_request_mdl-marketing_decline",
      },
    });
    onClose();
  }, [path, onClose]);

  return (
    <Box height="100%" width="100%">
      <Modal
        isOpen={isOpen}
        onClose={() => {
          customEvent({
            newGtm: {
              path,
              trigger_and_target: "file_request_mdl-close",
            },
            triggerHackle: true,
          });
          onClose();
        }}
        closeOnOverlayClick={false}
        scrollBehavior="inside"
        isCentered={true}
        useInert={false}
      >
        <ModalOverlay width="100%" height="100%" />
        <ModalContent minWidth="320px" backgroundColor={colors.white} backgroundSize="cover">
          <ModalBody padding="20px 24px 44px">
            {isCompleted ? (
              <VStack width="100%" gap={24}>
                <HStack width="100%" justifyContent="flex-end">
                  <div onClick={handleClose}>
                    <ColorIcon name="close" width={28} height={28} color={colors.black} />
                  </div>
                </HStack>
                <VStack width="100%" gap={12} alignItems="center">
                  <Box className="subtitle_21_b gray900" textAlign="center">
                    신청이 완료되었어요
                  </Box>
                  <Box className="caption_12_b gray700" textAlign="center">
                    마케팅 정보 수신에 동의하시면
                    <br />
                    장례 관련 혜택, 이벤트 소식을 보내드려요.
                  </Box>
                </VStack>
                <HStack width="100%" justifyContent="space-between" alignItems="center">
                  <HStack alignItems="center" gap={4}>
                    <Box className="caption_11_b" color={colors.gray400}>
                      선택
                    </Box>
                    <Box className="caption_11_b gray700">마케팅 정보 수신 동의</Box>
                  </HStack>
                  <a
                    className="caption_11_b"
                    href="https://goifuneral.notion.site/3d74caff865d48ccbf32d9a0ad3df80b"
                    target="_blank"
                  >
                    보기
                  </a>
                </HStack>
                <HStack width="100%" gap={8}>
                  <Button
                    flex={1}
                    backgroundColor={colors.gray100}
                    color={colors.gray700}
                    onClick={handleClose}
                  >
                    닫기
                  </Button>
                  <Button
                    flex={1}
                    backgroundColor={colors.orange650}
                    color={colors.white}
                    onClick={handleMarketingAgree}
                  >
                    동의하기
                  </Button>
                </HStack>
              </VStack>
            ) : (
              <VStack width="100%" gap={10}>
                <HStack width="100%" justifyContent="end" alignItems="center">
                  <HStack width="100%" alignItems="center" gap={8}>
                    {isTitleIcon && (
                      <ColorIcon name="list" color={colors.gray900} width={16} height={16} />
                    )}
                    <Box className="subtitle_21_b gray900" whiteSpace="pre-line">
                      {title}
                    </Box>
                  </HStack>
                  <div
                    onClick={() => {
                      customEvent({
                        newGtm: {
                          path,
                          trigger_and_target: "file_request_mdl-close",
                        },
                        triggerHackle: true,
                      });
                      onClose();
                    }}
                  >
                    <ColorIcon name="close" width={28} height={28} color={colors.black} />
                  </div>
                </HStack>
                <VStack width="100%" gap={24}>
                  <VStack gap={12} width="100%" className="body_16_m gray800">
                    <Box className="caption_12_b" whiteSpace="pre-line">
                      {description}
                    </Box>
                  </VStack>
                  <Input
                    value={value}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setValue(formatted);
                    }}
                    maxLength={13}
                    type="tel"
                    border={`1px solid ${colors.gray400}`}
                    width="100%"
                    placeholder="010-****-****"
                  />
                  {isStatusSelect && !isAdditionalInfoSelect && (
                    <Box>
                      <Box className="gray700 subtitle_14_b" pb="12px">
                        상황을 알려주세요(맞춤형 안내)
                      </Box>
                      <Field.Radio
                        title=""
                        items={[
                          "미리 준비하려고 해요",
                          "임종이 임박하신 상태입니다.",
                          "임종하신 상태입니다.",
                        ]}
                        value={memo}
                        onChange={(e) => {
                          customEvent({
                            newGtm: {
                              path: "match_request",
                              trigger_and_target: "recommendation_memo-option",
                              text: e,
                            },
                          });
                          setMemo(e);
                        }}
                        radioWidth="100%"
                        className="body_14_r"
                        flexDirection="column"
                        alignItem="center"
                      />
                    </Box>
                  )}
                  {isStatusSelect && isAdditionalInfoSelect && (
                    <Box
                      border={`1px solid ${colors.orange650}`}
                      borderRadius="8px"
                      padding="16px"
                      width="100%"
                    >
                      <Box className="subtitle_14_b" color={colors.orange650} pb="12px">
                        맞춤형 안내
                      </Box>
                      <Box>
                        <Box className="gray700 subtitle_14_b" pb="8px">
                          상황을 알려주세요
                        </Box>
                        <Field.Radio
                          title=""
                          items={[
                            "미리 준비하려고 해요",
                            "임종이 임박하신 상태입니다.",
                            "임종하신 상태입니다.",
                          ]}
                          value={memo}
                          onChange={(e) => {
                            customEvent({
                              newGtm: {
                                path: "match_request",
                                trigger_and_target: "recommendation_memo-option",
                                text: e,
                              },
                            });
                            setMemo(e);
                          }}
                          radioWidth="100%"
                          className="body_14_r"
                          flexDirection="column"
                          alignItem="center"
                          size={20}
                          gap={8}
                        />
                      </Box>
                      <Box mt="16px">
                        <Box className="gray700 subtitle_14_b" pb="8px">
                          추가로 필요한 정보가 있으신가요?
                        </Box>
                        <Field.Radio
                          title=""
                          items={["전체 장례 절차", "항목별 비용 기준"]}
                          value={additionalInfo}
                          onChange={(e) => {
                            customEvent({
                              newGtm: {
                                path,
                                trigger_and_target: "file_request_mdl-info_opt",
                                text: e,
                              },
                            });
                            setAdditionalInfo(e);
                          }}
                          radioWidth="100%"
                          className="body_14_r"
                          flexDirection="column"
                          alignItem="center"
                          size={20}
                          gap={8}
                        />
                      </Box>
                    </Box>
                  )}

                  <VStack width="100%" gap={8}>
                    <HStack alignItems="center" justifyContent="space-between">
                      <HStack alignItems="center" gap={4}>
                        <Box className="caption_11_b gray400">필수</Box>
                        <Box className="caption_11_b gray700">개인정보 수집, 이용, 제공 동의</Box>
                      </HStack>
                      <a
                        className="caption_11_b"
                        href="https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d"
                        target="_blank"
                      >
                        보기
                      </a>
                    </HStack>

                    {isNight && (
                      <HStack alignItems="center" justifyContent="space-between">
                        <HStack alignItems="center" gap={4}>
                          <Box className="caption_11_b gray400">필수</Box>
                          <Box className="caption_11_b gray700">긴급 상황 시 수신 동의(야간)</Box>
                        </HStack>
                        <a
                          className="caption_11_b"
                          href="https://goifuneral.notion.site/3e893cfdb8584fb28a4086a60aef5ad4"
                          target="_blank"
                        >
                          보기
                        </a>
                      </HStack>
                    )}
                  </VStack>

                  <Button
                    width="100%"
                    backgroundColor={colors.orange650}
                    color={colors.white}
                    onClick={handleClick}
                  >
                    {buttonText}
                  </Button>
                </VStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
